import CloseIcon from "@mui/icons-material/Close";
import { Chip, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { FC } from "react";
import * as Yup from "yup";
import AsyncSelect, {
  AsyncSelectValue,
} from "../../../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomTimePicker from "../../../../../../../components/CustomTimePicker";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useEducationMaterialList } from "../../../../../../../queries/education-material/use-education-material-list";
import { useEducationMaterialShareWith } from "../../../../../../../queries/education-material/use-education-material-share-with";
import { DEF_PAGINATION, getXtenantId } from "../../../../../../../utils/utils";

interface AssignEducationMaterialProps {
  disclosureHost: DisclosureHost;
}

interface SelectedDocument {
  uuid: string;
  name: string;
  description?: string;
}

interface FormValues {
  documents: AsyncSelectValue | null;
  scheduleDateTime: string | null;
  selectedDocuments: SelectedDocument[];
}

const initialValues: FormValues = {
  documents: null,
  scheduleDateTime: null,
  selectedDocuments: [],
};

const validationSchema = Yup.object({});
//   selectedDocuments: Yup.array().min(1, "Please select at least one document"),
//   scheduleDateTime: Yup.date()
//     .nullable()
//     .required("Schedule date and time is required"),
// });

const AssignEducationMaterial: FC<AssignEducationMaterialProps> = ({
  disclosureHost,
}) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [searchKey, setSearchKey] = useDebounce<string>("");

  const { data: educationMaterialList, isLoading } = useEducationMaterialList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    searchString: searchKey,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  const { mutateAsync: shareEducationMaterial } =
    useEducationMaterialShareWith();

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Share each selected document with the patient
        const sharePromises = values.selectedDocuments.map((doc) =>
          shareEducationMaterial({
            patientEducationMaterial: {
              uuid: doc.uuid,
              patients: disclosureHost.data?.patientId
                ? {
                    [disclosureHost.data.patientId]:
                      disclosureHost.data.patientId,
                  }
                : {},
            },
            xTENANTID: getXtenantId(),
          })
        );

        await Promise.all(sharePromises);

        showSnackbar({
          message: "Education materials assigned successfully",
          type: "success",
        });

        // Refresh education material list
        queryClient.invalidateQueries({
          queryKey: ["education-material-list"],
        });

        handleClose();
      } catch (error) {
        showSnackbar({
          message: "Failed to assign education materials",
          type: "error",
        });
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const handleDocumentSelect = (selectedDoc: AsyncSelectValue | null) => {
    if (selectedDoc) {
      const document = educationMaterialList?.data?.content?.find(
        (doc) => doc.uuid === selectedDoc.value
      );

      if (
        document &&
        !formik.values.selectedDocuments.find(
          (doc) => doc.uuid === document.uuid
        )
      ) {
        formik.setFieldValue("selectedDocuments", [
          ...formik.values.selectedDocuments,
          {
            uuid: document.uuid,
            name: document.name,
            description: document.description,
          },
        ]);
      }

      // Clear the selection
      formik.setFieldValue("documents", null);
    }
  };

  const handleRemoveDocument = (uuid: string) => {
    formik.setFieldValue(
      "selectedDocuments",
      formik.values.selectedDocuments.filter((doc) => doc.uuid !== uuid)
    );
  };

  const handleDateTimeChange = (
    date: dayjs.Dayjs | null,
    type: "date" | "time"
  ) => {
    if (type === "time" && formik.values.scheduleDateTime) {
      const preDate = new Date(formik.values.scheduleDateTime);
      preDate?.setHours(
        date?.hour() || 0,
        date?.minute() || 0,
        date?.second() || 0
      );
      formik.setFieldValue("scheduleDateTime", preDate?.toISOString());
    } else {
      formik.setFieldValue("scheduleDateTime", date?.toISOString() || null);
    }
  };

  const educationMaterialOptions =
    educationMaterialList?.data?.content?.map((doc) => ({
      value: doc.uuid,
      label: doc.name,
      description: doc.description,
    })) || [];

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Assign Education Material"
      dialogAction={
        <Flex gap={2} justifyContent="flex-end">
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          <StyledButtonV2
            label="Assign"
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={
              formik.isSubmitting ||
              formik.values.selectedDocuments.length === 0
            }
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "30vw",
          height: "250px",
        },
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{
          pt: 2,
        }}
      >
        {/* Document Selection */}
        <Grid size={12}>
          <AsyncSelect
            label="Document"
            placeholder="Search & Select Documents"
            options={educationMaterialOptions}
            value={formik.values.documents}
            onChange={handleDocumentSelect}
            onSearch={setSearchKey}
            loading={isLoading}
            error={formik.touched.documents && formik.errors.documents}
          />
        </Grid>

        {/* Schedule Date & Time */}
        <Grid size={12}>
          <Flex gap={2}>
            <CustomDatePicker
              label="Schedule Send"
              placeholder="Select Date"
              value={
                formik.values.scheduleDateTime
                  ? dayjs(formik.values.scheduleDateTime)
                  : null
              }
              onChange={(val) => handleDateTimeChange(val, "date")}
              minDate={dayjs()}
              error={
                formik.touched.scheduleDateTime &&
                formik.errors.scheduleDateTime
              }
            />
            <CustomTimePicker
              label="Time"
              placeholder="Select Time"
              value={formik.values.scheduleDateTime}
              onChange={(val) => handleDateTimeChange(val, "time")}
              error={
                formik.touched.scheduleDateTime &&
                formik.errors.scheduleDateTime
              }
            />
          </Flex>
        </Grid>

        {/* Selected Documents */}
        {formik.values.selectedDocuments.length > 0 && (
          <Grid size={12}>
            <Text variant="subtitle1" fontWeight="medium" mb={2}>
              Selected Documents:
            </Text>
            <Flex gap={1} flexWrap="wrap">
              {formik.values.selectedDocuments.map((doc) => (
                <Chip
                  key={doc.uuid}
                  label={doc.name}
                  onDelete={() => handleRemoveDocument(doc.uuid)}
                  deleteIcon={<CloseIcon />}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Flex>
            {formik.touched.selectedDocuments &&
              formik.errors.selectedDocuments && (
                <Text fontSize="13px" color="error" mt={1}>
                  {typeof formik.errors.selectedDocuments === "string"
                    ? formik.errors.selectedDocuments
                    : "Please select at least one document"}
                </Text>
              )}
          </Grid>
        )}
      </Grid>
    </StyledFormModal>
  );
};

export default AssignEducationMaterial;
